import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import ReactFlow, {
  MiniMap,
  Background,
  ReactFlowProvider,
  Position,
  Handle,
  NodeResizer,
  MarkerType,
  useNodesState,
  useEdgesState,
  type NodeProps,
  type Node,
  type Edge,
  type ReactFlowInstance,
  type OnMove,
  type NodeChange,
  type OnNodesChange,
  EdgeProps,
  useReactFlow,
  BaseEdge,
  EdgeLabelRenderer,
  getSmoothStepPath,
  getStraightPath,
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import useWindowSize from '../../hooks/useWindowSize';
import { useFetchTables } from '../../hooks/toolbox-hooks';

const HARBOR_INTERACT_BG = '#E3EEFF';
const HARBOR_INTERACT_BG_MEDIUM = '#CCE1FF';
const HARBOR_INTERACT_BG_WEAK = '#BAD6FF';
const HARBOR_NEUTRAL_BG_MEDIUM = '#F7F7F7';
const HARBOR_NEUTRAL_BG_WEAK = '#E1E4E8';
const HARBOR_INTERACT_BORDER = '#2774D9';
const HARBOR_NEUTRAL_BORDER = '#889099';
const HARBOR_INTERACT_TEXT = '#1D69CC';
const HARBOR_NEUTRAL_TEXT = '#23282E';
const HARBOR_BLUE = '#2176ae';

const HIGHLIGHT_NODE_BG = '#FFE4B5';
const HIGHLIGHT_NODE_BORDER = '#FF8C00';
const HIGHLIGHT_EDGE_COLOR = '#FF4500';
const SELECTED_NODE_BG = '#FFD700';
const SELECTED_NODE_BORDER = '#DC143C';
const SELECTED_EDGE_COLOR = '#DC143C';

const NODE_BG_PALETTE = [
  HARBOR_INTERACT_BG,
  HARBOR_INTERACT_BG_MEDIUM,
  HARBOR_INTERACT_BG_WEAK,
  HARBOR_NEUTRAL_BG_MEDIUM,
  HARBOR_NEUTRAL_BG_WEAK,
];
const NODE_BORDER_PALETTE = [
  HARBOR_INTERACT_BORDER,
  HARBOR_NEUTRAL_BORDER
];
const NODE_TEXT_PALETTE = [
  HARBOR_NEUTRAL_TEXT,
  HARBOR_INTERACT_TEXT
];

const tooltipStyle: React.CSSProperties = {
  position: 'fixed',
  background: '#fff',
  color: '#222',
  padding: '12px 16px',
  border: '1px solid #888',
  borderRadius: 8,
  boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
  pointerEvents: 'none',
  zIndex: 200,
  fontFamily: 'Arial, sans-serif',
  fontSize: 14,
  maxWidth: 400,
  wordBreak: 'break-all',
  display: 'block'
};

function calculateOptimalGrid(nodeCount: number, containerWidth: number) {
  if (nodeCount === 0) return { cols: 1, rows: 0 };

  const nodeWidth = 280;
  const gapX = 5;
  const colWidth = nodeWidth + gapX;

  const maxColsByScreen = Math.max(1, Math.floor(containerWidth / colWidth));

  let bestCols = Math.ceil(nodeCount / maxColsByScreen);
  let bestRows = maxColsByScreen;

  for (let testCols = bestCols; testCols <= Math.min(nodeCount, 20); testCols++) {
    const testRows = Math.ceil(nodeCount / testCols);
    if (testCols >= testRows) {
      bestCols = testCols;
      bestRows = testRows;
      break;
    }
  }

  const cols = bestCols;
  const rows = bestRows;

  return { cols, rows };
}

function getCanonical(s: string | undefined): string {
  return String(s || '').replace(/[\s_-]+/g, '').toLowerCase();
}

const ResizableNode = (props: NodeProps) => {
  const { data, selected } = props;
  const nodeStyle = data?.style ?? {};
  return (
    <div style={{
      ...nodeStyle,
      position: 'relative',
      minHeight: 60,
      minWidth: 160,
      cursor: 'move'
    }}>
      <NodeResizer minWidth={160} minHeight={60} isVisible={selected} color={HARBOR_INTERACT_BORDER} />
      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.fields) }} />
      <Handle type="source" position={Position.Right} />
      <Handle type="target" position={Position.Left} />
      <Handle type="source" position={Position.Top} />
      <Handle type="target" position={Position.Bottom} />
    </div>
  );
};
const nodeTypes = { resizable: ResizableNode };

function getNodeCenter(node: Node): { x: number; y: number } {
  const width = node.width || 280;
  const height = node.height || 120;
  return {
    x: node.position.x + width / 2,
    y: node.position.y + height / 2,
  };
}

function getSideCenter(node: Node, side: Position) {
  const x = node.position.x;
  const y = node.position.y;
  const width = node.width || 280;
  const height = node.height || 120;
  switch (side) {
    case Position.Top: return { x: x + width / 2, y: y };
    case Position.Bottom: return { x: x + width / 2, y: y + height };
    case Position.Left: return { x: x, y: y + height / 2 };
    case Position.Right: return { x: x + width, y: y + height / 2 };
    default: return { x: x + width / 2, y: y + height / 2 };
  }
}

function getClosestSidePoint(source: Node, target: Node) {
  const sides = [Position.Top, Position.Right, Position.Bottom, Position.Left];
  const targetCenter = getNodeCenter(target);
  let minDist = Infinity, minSide = Position.Right, minPoint = { x: 0, y: 0 };
  for (const side of sides) {
    const pt = getSideCenter(source, side);
    const dist = Math.hypot(pt.x - targetCenter.x, pt.y - targetCenter.y);
    if (dist < minDist) {
      minDist = dist;
      minSide = side;
      minPoint = pt;
    }
  }
  return { side: minSide, point: minPoint };
}

// Dynamic edge component that recalculates on every render
const DynamicClosestSideEdge: React.FC<EdgeProps> = (props) => {
  const { id, source, target, style, markerEnd, label } = props;
  const { getNodes } = useReactFlow();
  const nodes = getNodes();
  const sourceNode = nodes.find(n => n.id === source);
  const targetNode = nodes.find(n => n.id === target);

  if (!sourceNode || !targetNode) return null;

  const sourceInfo = getClosestSidePoint(sourceNode, targetNode);
  const targetInfo = getClosestSidePoint(targetNode, sourceNode);

  const path = `M${sourceInfo.point.x},${sourceInfo.point.y} L${targetInfo.point.x},${targetInfo.point.y}`;

  return (
    <>
      <BaseEdge
        id={id}
        style={style}
        markerEnd={markerEnd}
        path={path}
      />
      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${(sourceInfo.point.x + targetInfo.point.x) / 2}px, ${(sourceInfo.point.y + targetInfo.point.y) / 2}px)`,
              pointerEvents: 'all',
              fontSize: 10,
              background: '#fff',
              padding: '2px 4px',
              borderRadius: 3,
              border: '1px solid #ccc'
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

// Dynamic Step Edge
const DynamicStepEdge: React.FC<EdgeProps> = (props) => {
  const { id, source, target, style, markerEnd, label } = props;
  const { getNodes } = useReactFlow();
  const nodes = getNodes();
  const sourceNode = nodes.find(n => n.id === source);
  const targetNode = nodes.find(n => n.id === target);

  if (!sourceNode || !targetNode) return null;

  const sourceInfo = getClosestSidePoint(sourceNode, targetNode);
  const targetInfo = getClosestSidePoint(targetNode, sourceNode);

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX: sourceInfo.point.x,
    sourceY: sourceInfo.point.y,
    sourcePosition: sourceInfo.side,
    targetX: targetInfo.point.x,
    targetY: targetInfo.point.y,
    targetPosition: targetInfo.side,
  });

  return (
    <>
      <BaseEdge
        id={id}
        style={style}
        markerEnd={markerEnd}
        path={edgePath}
      />
      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'all',
              fontSize: 10,
              background: '#fff',
              padding: '2px 4px',
              borderRadius: 3,
              border: '1px solid #ccc'
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

// Dynamic Smooth Step Edge
const DynamicSmoothStepEdge: React.FC<EdgeProps> = (props) => {
  const { id, source, target, style, markerEnd, label } = props;
  const { getNodes } = useReactFlow();
  const nodes = getNodes();
  const sourceNode = nodes.find(n => n.id === source);
  const targetNode = nodes.find(n => n.id === target);

  if (!sourceNode || !targetNode) return null;

  const sourceInfo = getClosestSidePoint(sourceNode, targetNode);
  const targetInfo = getClosestSidePoint(targetNode, sourceNode);

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX: sourceInfo.point.x,
    sourceY: sourceInfo.point.y,
    sourcePosition: sourceInfo.side,
    targetX: targetInfo.point.x,
    targetY: targetInfo.point.y,
    targetPosition: targetInfo.side,
    borderRadius: 8,
  });

  return (
    <>
      <BaseEdge
        id={id}
        style={style}
        markerEnd={markerEnd}
        path={edgePath}
      />
      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'all',
              fontSize: 10,
              background: '#fff',
              padding: '2px 4px',
              borderRadius: 3,
              border: '1px solid #ccc'
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

// Dynamic Straight Edge
const DynamicStraightEdge: React.FC<EdgeProps> = (props) => {
  const { id, source, target, style, markerEnd, label } = props;
  const { getNodes } = useReactFlow();
  const nodes = getNodes();
  const sourceNode = nodes.find(n => n.id === source);
  const targetNode = nodes.find(n => n.id === target);

  if (!sourceNode || !targetNode) return null;

  const sourceInfo = getClosestSidePoint(sourceNode, targetNode);
  const targetInfo = getClosestSidePoint(targetNode, sourceNode);

  const [edgePath, labelX, labelY] = getStraightPath({
    sourceX: sourceInfo.point.x,
    sourceY: sourceInfo.point.y,
    targetX: targetInfo.point.x,
    targetY: targetInfo.point.y,
  });

  return (
    <>
      <BaseEdge
        id={id}
        style={style}
        markerEnd={markerEnd}
        path={edgePath}
      />
      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'all',
              fontSize: 10,
              background: '#fff',
              padding: '2px 4px',
              borderRadius: 3,
              border: '1px solid #ccc'
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

const edgeTypes = {
  closestSide: DynamicClosestSideEdge,
  step: DynamicStepEdge,
  smoothstep: DynamicSmoothStepEdge,
  default: DynamicStraightEdge,
};

function useZoomHold(rfInstance: ReactFlowInstance | null, setZoom: (zoom: number) => void) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const ZOOM_STEP = 0.05;
  const ZOOM_MIN = 0.05;
  const ZOOM_MAX = 4;
  const ZOOM_HOLD_INTERVAL = 50;
  const zoomBy = (delta: number) => {
    if (!rfInstance) return;
    const newZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, rfInstance.getZoom() + delta));
    setZoom(newZoom);
    rfInstance.zoomTo(newZoom);
  };
  const startHold = (delta: number) => {
    zoomBy(delta);
    intervalRef.current = setInterval(() => zoomBy(delta), ZOOM_HOLD_INTERVAL);
  };
  const stopHold = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };
  return { zoomBy, startHold, stopHold, ZOOM_STEP, ZOOM_MIN, ZOOM_MAX };
}

function getMaxTextWidth(nodes: any[]): number {
  if (typeof window === 'undefined') return 60;
  const font = '15px Arial, sans-serif';
  let maxWidth = 0;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return 60;
  ctx.font = font;

  nodes.forEach(node => {
    const labelWidth = ctx.measureText(node.data.label || '').width;
    if (labelWidth > maxWidth) maxWidth = labelWidth;
    const div = document.createElement('div');
    div.innerHTML = DOMPurify.sanitize(node.data.fields);
    div.style.display = 'none';
    document.body.appendChild(div);
    div.querySelectorAll('div').forEach(divChild => {
      const text = divChild.textContent || '';
      const width = ctx.measureText(text).width;
      if (width > maxWidth) maxWidth = width;
    });
    document.body.removeChild(div);
  });
  return Math.ceil(maxWidth) + 40;
}

interface DatabaseViewerProps {
  isVisible?: boolean;
}

function DatabaseViewer({ isVisible = true }: DatabaseViewerProps) {
  const { width, height } = useWindowSize();
  const { data, error } = useFetchTables({detailed: true});

  const [tooltip, setTooltip] = useState<{content: string; x: number; y: number} | null>(null);
  const [rfInstance, setRfInstance] = useState<ReactFlowInstance | null>(null);
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1);
  const [layoutType, setLayoutType] = useState<"grid" | "hierarchical" | "circle">("grid");
  const [edgeType, setEdgeType] = useState<'default' | 'smoothstep' | 'step' | 'closestSide'>('step');
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedTooltipPos, setSelectedTooltipPos] = useState<{x: number; y: number} | null>(null);
  const [selectedModel, setSelectedModel] = useState('all');
  const [relationDepth, setRelationDepth] = useState(1);
  const [isInitialized, setIsInitialized] = useState(false);
  const isRecenteringRef = useRef(false);
  const prevVisibilityRef = useRef(isVisible);

  const baseData = useMemo(() => {
    if (!data?.models) return { nodes: [], edges: [] };
    const models = data.models;
    const modelMap: Record<string, any> = {};
    models.forEach((m: any) => { modelMap[m.model_name] = m; });

    const baseNodes = models.map((model: any, idx: number) => {
      const html = `
        <div>
          <b>${model.label || model.model_name}</b>
        </div>
        <hr style="margin:2px 0" />
        ${
        model.fields.map((f: any) =>
          `<div>
              <span style='color:#333;'><b>${f.label || f.key}</b></span>
              : <span style='color:#596069;'>${f.type}${f.primary_key ? ' (PK)' : ''}${f.is_relation ? ' (Relation)' : ''}</span>
            </div>`
        ).join('')
      }
      `;
      const originalStyle = {
        background: NODE_BG_PALETTE[idx % NODE_BG_PALETTE.length],
        border: `2px solid ${NODE_BORDER_PALETTE[idx % NODE_BORDER_PALETTE.length]}`,
        borderRadius: 8,
        fontSize: 15,
        padding: 12,
        color: NODE_TEXT_PALETTE[idx % NODE_TEXT_PALETTE.length]
      };
      return {
        id: model.model_name,
        type: 'resizable',
        data: {
          label: model.label || model.model_name,
          fields: html,
          style: originalStyle,
          originalStyle,
        },
        position: { x: 0, y: 0 },
        width: 280,
        height: Math.max(120, 60 + (model.fields.length * 20)),
        draggable: true,
        selectable: true,
      };
    });

    const edges: Edge[] = [];
    models.forEach((model: any) => {
      model.fields.forEach((field: any) => {
        if (field.is_relation && field.type && field.label) {
          let relatedModel =
            models.find((m: any) =>
              getCanonical(m.label) === getCanonical(field.label) ||
              getCanonical(m.model_name) === getCanonical(field.label)
            );
          if (!relatedModel && modelMap[field.label]) {
            relatedModel = modelMap[field.label];
          }
          if (relatedModel) {
            edges.push({
              id: `${model.model_name}-${field.key}-${relatedModel.model_name}`,
              source: model.model_name,
              target: relatedModel.model_name,
              label: field.label,
              type: edgeType,
              animated: false,
              style: { stroke: HARBOR_INTERACT_BORDER, strokeWidth: 2 },
              markerEnd: { type: MarkerType.ArrowClosed, color: HARBOR_INTERACT_BORDER },
              data: { relation: field.type, originalStyle: { stroke: HARBOR_INTERACT_BORDER, strokeWidth: 2 } }
            });
          }
        }
      });
    });
    return { nodes: baseNodes, edges };
  }, [data, edgeType]);

  const filteredData = useMemo(() => {
    if (selectedModel === 'all') return { nodes: baseData.nodes, edges: baseData.edges };
    const visited = new Set<string>();
    const queue = [{ id: selectedModel, depth: 0 }];
    while (queue.length > 0) {
      const item = queue.shift();
      if (!item) continue;
      const { id: current, depth } = item;
      if (!current || visited.has(current) || depth > relationDepth) continue;
      visited.add(current);
      baseData.edges.forEach(e => {
        if (e.source === current && !visited.has(e.target) && depth < relationDepth)
          queue.push({ id: e.target, depth: depth + 1 });
        if (e.target === current && !visited.has(e.source) && depth < relationDepth)
          queue.push({ id: e.source, depth: depth + 1 });
      });
    }
    const subNodes = baseData.nodes.filter(n => visited.has(n.id));
    const subEdges = baseData.edges.filter(e => visited.has(e.source) && visited.has(e.target));
    return { nodes: subNodes, edges: subEdges };
  }, [baseData, selectedModel, relationDepth]);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges] = useEdgesState([]);

  const recenterToDefaultLayout = useCallback(() => {
    if (!rfInstance || isRecenteringRef.current) return;
    isRecenteringRef.current = true;
    const nodesToLayout = filteredData.nodes;
    let positionedNodes: Node[];

    let gapX = 60, gapY = 60;
    if (layoutType === "grid" && nodesToLayout.length > 0) {
      try {
        const maxTextWidth = Math.max(60, Math.min(getMaxTextWidth(nodesToLayout), 320));
        gapX = Math.max(30, Math.floor(maxTextWidth / 2));
        gapY = gapX;
      } catch (e) {
        gapX = 30;
        gapY = 30;
      }
    }

    if (layoutType === "hierarchical") {
      const g = new dagre.graphlib.Graph();
      g.setDefaultEdgeLabel(() => ({}));
      g.setGraph({
        rankdir: "TB",
        nodesep: 50,
        ranksep: 80,
        marginx: 40,
        marginy: 40
      });
      nodesToLayout.forEach((node) => {
        g.setNode(node.id, { width: node.width || 280, height: node.height || 120 });
      });
      filteredData.edges.forEach((edge) => {
        g.setEdge(edge.source, edge.target);
      });
      dagre.layout(g);
      positionedNodes = nodesToLayout.map((node) => {
        const pos = g.node(node.id);
        return {
          ...node,
          position: {
            x: pos.x - (node.width || 280) / 2,
            y: pos.y - (node.height || 120) / 2
          }
        };
      });
    } else if (layoutType === "circle") {
      const containerWidth = width || 1600;
      const centerX = containerWidth / 2;
      const centerY = 500;
      const nodeWidth = 280;
      const padding = 60;
      const minCircumference = nodesToLayout.length * (nodeWidth + padding);
      const minRadius = nodesToLayout.length === 0 ? 1 : minCircumference / (2 * Math.PI);
      const radius = Math.max(minRadius, 400);
      positionedNodes = nodesToLayout.map((node, i) => {
        const angle = (2 * Math.PI * i) / nodesToLayout.length;
        return {
          ...node,
          position: {
            x: centerX + Math.cos(angle) * radius - (node.width || 280) / 2,
            y: centerY + Math.sin(angle) * radius - (node.height || 120) / 2
          }
        };
      });
    } else {
      const containerWidth = width || 1600;
      const nodeWidth = 280;
      const nodeHeight = 160;
      const startX = 50;
      const startY = 100;
      const { cols } = calculateOptimalGrid(nodesToLayout.length, containerWidth);

      positionedNodes = nodesToLayout.map((node, idx) => {
        const col = idx % cols;
        const row = Math.floor(idx / cols);

        const x = startX + col * (nodeWidth + gapX);
        const y = startY + row * (nodeHeight + gapY);

        return {
          ...node,
          position: { x, y },
          data: {
            ...node.data,
            style: node.data.originalStyle
          }
        };
      });
    }

    setNodes([]);
    setTimeout(() => {
      setNodes(positionedNodes);
      setEdges(filteredData.edges);
      setSelectedNodeId(null);
      setSelectedTooltipPos(null);
      setTooltip(null);
      setTimeout(() => {
        if (!rfInstance) {
          isRecenteringRef.current = false;
          return;
        }
        if (positionedNodes.length > 0) {
          rfInstance.fitView({ padding: 0.1, duration: 800, maxZoom: 0.88 });
        }
        setTimeout(() => {
          if (rfInstance) {
            const currentZoom = rfInstance.getZoom();
            setZoom(currentZoom);
            zoomRef.current = currentZoom;
          }
          isRecenteringRef.current = false;
        }, 100);
      }, 200);
    }, 50);
  }, [rfInstance, filteredData, layoutType, width, setNodes, setEdges]);

  useEffect(() => {
    if (isVisible && !prevVisibilityRef.current && rfInstance && filteredData.nodes.length > 0) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          recenterToDefaultLayout();
        });
      });
    }
    prevVisibilityRef.current = isVisible;
  }, [isVisible, rfInstance, filteredData.nodes.length, recenterToDefaultLayout]);

  const prevControlsRef = useRef({ selectedModel: '', relationDepth: 0, layoutType: '', nodeCount: 0, edgeType: '' });
  useEffect(() => {
    if (!isVisible) return;
    const currentControls = {
      selectedModel,
      relationDepth,
      layoutType,
      nodeCount: filteredData.nodes.length,
      edgeType
    };
    const prevControls = prevControlsRef.current;
    const hasChanged =
      currentControls.selectedModel !== prevControls.selectedModel ||
      currentControls.relationDepth !== prevControls.relationDepth ||
      currentControls.layoutType !== prevControls.layoutType ||
      currentControls.nodeCount !== prevControls.nodeCount ||
      currentControls.edgeType !== prevControls.edgeType;
    if (hasChanged && isInitialized) {
      setTimeout(() => {
        recenterToDefaultLayout();
      }, 100);
    }
    prevControlsRef.current = currentControls;
  }, [selectedModel, relationDepth, layoutType, filteredData.nodes.length, isInitialized, recenterToDefaultLayout, isVisible, edgeType]);

  useEffect(() => {
    if (filteredData.nodes.length > 0 && !isInitialized && isVisible && rfInstance) {
      setIsInitialized(true);
      setTimeout(() => {
        recenterToDefaultLayout();
      }, 200);
    }
  }, [filteredData.nodes.length, isInitialized, recenterToDefaultLayout, isVisible, rfInstance]);

  const handleNodesChange = useCallback<OnNodesChange>((changes: NodeChange[]) => {
    const filteredChanges = changes.filter(change =>
      change.type !== 'remove'
    );
    onNodesChange(filteredChanges);
  }, [onNodesChange]);

  const getRelatedElements = useCallback((nodeId: string) => {
    const relatedNodeIds = new Set<string>();
    const relatedEdgeIds = new Set<string>();
    edges.forEach(edge => {
      if (edge.source === nodeId || edge.target === nodeId) {
        relatedEdgeIds.add(edge.id);
        relatedNodeIds.add(edge.source);
        relatedNodeIds.add(edge.target);
      }
    });
    return { relatedNodeIds, relatedEdgeIds };
  }, [edges]);

  const applyHighlighting = useCallback((nodeId: string, isSelected = false) => {
    const { relatedNodeIds, relatedEdgeIds } = getRelatedElements(nodeId);
    const highlightBg = isSelected ? SELECTED_NODE_BG : HIGHLIGHT_NODE_BG;
    const highlightBorder = isSelected ? SELECTED_NODE_BORDER : HIGHLIGHT_NODE_BORDER;
    const highlightEdge = isSelected ? SELECTED_EDGE_COLOR : HIGHLIGHT_EDGE_COLOR;

    setNodes((currentNodes) => currentNodes.map(n => {
      const isMainNode = n.id === nodeId;
      const isRelatedNode = relatedNodeIds.has(n.id);
      if (isMainNode || isRelatedNode) {
        return {
          ...n,
          data: {
            ...n.data,
            style: {
              ...n.data.originalStyle,
              background: highlightBg,
              border: `${isMainNode ? 3 : 2}px solid ${highlightBorder}`,
              boxShadow: isMainNode ? `0 4px 12px rgba(${isSelected ? '220, 20, 60' : '255, 140, 0'}, 0.4)` : undefined
            }
          }
        };
      }
      return {
        ...n,
        data: {
          ...n.data,
          style: n.data.originalStyle
        }
      };
    }));

    setEdges((currentEdges) => currentEdges.map(e => {
      if (relatedEdgeIds.has(e.id)) {
        return {
          ...e,
          style: { stroke: highlightEdge, strokeWidth: 3 },
          animated: true
        };
      }
      return {
        ...e,
        style: e.data?.originalStyle || { stroke: HARBOR_INTERACT_BORDER, strokeWidth: 2 },
        animated: false
      };
    }));
  }, [getRelatedElements, setNodes, setEdges]);

  const resetHighlighting = useCallback(() => {
    setNodes((currentNodes) => currentNodes.map(n => ({
      ...n,
      data: {
        ...n.data,
        style: n.data.originalStyle
      }
    })));
    setEdges((currentEdges) => currentEdges.map(e => ({
      ...e,
      style: e.data?.originalStyle || { stroke: HARBOR_INTERACT_BORDER, strokeWidth: 2 },
      animated: false
    })));
  }, [setNodes, setEdges]);

  const handleNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    event.stopPropagation();
    if (selectedNodeId === node.id) {
      setSelectedNodeId(null);
      setSelectedTooltipPos(null);
      setTooltip(null);
      resetHighlighting();
    } else {
      setSelectedNodeId(node.id);
      const tooltipPos = { x: event.clientX + 18, y: event.clientY - 12 };
      setSelectedTooltipPos(tooltipPos);
      setTooltip({
        content: node.data.fields,
        ...tooltipPos
      });
      applyHighlighting(node.id, true);
    }
  }, [selectedNodeId, resetHighlighting, applyHighlighting]);

  const handlePaneClick = useCallback(() => {
    if (selectedNodeId) {
      setSelectedNodeId(null);
      setSelectedTooltipPos(null);
      setTooltip(null);
      resetHighlighting();
    }
  }, [selectedNodeId, resetHighlighting]);

  const handleNodeMouseEnter = useCallback((event: React.MouseEvent, node: Node) => {
    setHoveredNodeId(node.id);
    if (!selectedNodeId) {
      setTooltip({
        content: node.data.fields,
        x: event.clientX + 18,
        y: event.clientY - 12
      });
      applyHighlighting(node.id, false);
    }
  }, [selectedNodeId, applyHighlighting]);

  const handleNodeMouseMove = useCallback((event: React.MouseEvent) => {
    if (!selectedNodeId && hoveredNodeId) {
      setTooltip(t => t ? { ...t, x: event.clientX + 18, y: event.clientY - 12 } : null);
    }
  }, [selectedNodeId, hoveredNodeId]);

  const handleNodeMouseLeave = useCallback(() => {
    setHoveredNodeId(null);
    if (!selectedNodeId) {
      setTooltip(null);
      resetHighlighting();
    }
  }, [selectedNodeId, resetHighlighting]);

  const { zoomBy, startHold, stopHold, ZOOM_STEP, ZOOM_MIN, ZOOM_MAX } = useZoomHold(rfInstance, setZoom);
  const sliderRef = useRef<HTMLInputElement>(null);
  const dragState = useRef({ dragging: false });

  const onSliderMouseDown = () => {
    dragState.current.dragging = false;
    const moveHandler = () => { dragState.current.dragging = true; };
    const upHandler = () => {
      setTimeout(() => {
        window.removeEventListener('mousemove', moveHandler);
        window.removeEventListener('mouseup', upHandler);
      }, 0);
    };
    window.addEventListener('mousemove', moveHandler);
    window.addEventListener('mouseup', upHandler);
  };

  const onSliderClick = (e: React.MouseEvent) => {
    if (dragState.current.dragging) return;
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percent = clickX / rect.width;
    const targetZoom = ZOOM_MIN + percent * (ZOOM_MAX - ZOOM_MIN);
    if (targetZoom > zoom) {
      zoomBy(ZOOM_STEP);
    } else if (targetZoom < zoom) {
      zoomBy(-ZOOM_STEP);
    }
    e.preventDefault();
    e.stopPropagation();
  };

  const handleZoomChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newZoom = parseFloat(e.target.value);
    setZoom(newZoom);
    zoomRef.current = newZoom;
    if (rfInstance) {
      rfInstance.zoomTo(newZoom);
    }
  }, [rfInstance]);

  const handleInit = useCallback((instance: ReactFlowInstance) => {
    setRfInstance(instance);
  }, []);

  const handleMove = useCallback<OnMove>(
    (_event, viewport) => {
      setZoom(viewport.zoom);
      zoomRef.current = viewport.zoom;
    }, []
  );

  if (error) return <div>Error: {String(error)}</div>;
  if (!data || !data.models) return <div>No data available.</div>;

  return (
    <div
      style={{
        position: "relative",
        width,
        height,
        minHeight: 400,
        minWidth: 400,
        overflow: "auto",
        background: HARBOR_NEUTRAL_BG_MEDIUM
      }}
    >
      <ReactFlowProvider>
        <div style={{
          position: "absolute",
          left: 24,
          top: 24,
          zIndex: 50,
          background: "#fff",
          borderRadius: 6,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          padding: "10px 14px",
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
          maxWidth: "calc(100% - 150px)"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 500, color: HARBOR_INTERACT_TEXT }}>Model:</label>
            <select
              value={selectedModel}
              onChange={e => setSelectedModel(e.target.value)}
              style={{
                color: HARBOR_INTERACT_TEXT,
                padding: "4px 8px",
                borderRadius: 4,
                border: "1px solid #ccc"
              }}
            >
              <option value="all">All Models</option>
              {data.models.map((m: any) => (
                <option key={m.model_name} value={m.model_name}>{m.label || m.model_name}</option>
              ))}
            </select>
          </div>

          {selectedModel !== 'all' && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: HARBOR_INTERACT_TEXT }}>
                Depth:
              </label>
              <input
                type="range"
                min={1}
                max={5}
                value={relationDepth}
                onChange={e => setRelationDepth(Number(e.target.value))}
                style={{ width: 80 }}
              />
              <span style={{
                minWidth: 16,
                textAlign: "center",
                fontWeight: 600,
                color: HARBOR_INTERACT_TEXT,
                fontSize: 13
              }}>
                {relationDepth}
              </span>
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 500, color: HARBOR_INTERACT_TEXT }}>Layout:</label>
            <select
              value={layoutType}
              onChange={e => setLayoutType(e.target.value as "grid" | "hierarchical" | "circle")}
              style={{
                color: HARBOR_INTERACT_TEXT,
                padding: "4px 8px",
                borderRadius: 4,
                border: "1px solid #ccc"
              }}
            >
              <option value="grid">Grid</option>
              <option value="hierarchical">Hierarchical</option>
              <option value="circle">Circle</option>
            </select>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 500, color: HARBOR_INTERACT_TEXT }}>Edge style:</label>
            <select
              value={edgeType}
              onChange={e => setEdgeType(e.target.value as any)}
              style={{
                color: HARBOR_INTERACT_TEXT,
                padding: "4px 8px",
                borderRadius: 4,
                border: "1px solid #ccc"
              }}
            >
              <option value="step">Right-angle (Step)</option>
              <option value="smoothstep">Curved (SmoothStep)</option>
              <option value="default">Straight</option>
              <option value="closestSide">Closest Sides (straight)</option>
            </select>
          </div>

          <button
            style={{
              background: HARBOR_BLUE,
              color: 'white',
              border: 'none',
              borderRadius: 5,
              padding: '6px 12px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer'
            }}
            onClick={recenterToDefaultLayout}
            title="Reset to default layout and center view"
          >
            Reset Layout
          </button>
        </div>

        <div
          style={{
            position: "absolute",
            top: 24,
            right: 24,
            zIndex: 40,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 4,
            background: "rgba(255,255,255,0.95)",
            borderRadius: 10,
            padding: "8px 6px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            width: 44,
          }}
        >
          <button
            style={{
              fontSize: 16,
              padding: "2px 8px",
              borderRadius: 5,
              border: "none",
              background: HARBOR_BLUE,
              color: "#fff",
              cursor: "pointer",
              fontWeight: "bold",
              width: 32,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            title="Zoom In"
            onClick={() => zoomBy(ZOOM_STEP)}
            onMouseDown={() => startHold(ZOOM_STEP)}
            onMouseUp={stopHold}
            onMouseLeave={stopHold}
            onTouchStart={() => startHold(ZOOM_STEP)}
            onTouchEnd={stopHold}
            onTouchCancel={stopHold}
          >+</button>

          <div style={{
            height: 120,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            width: 32
          }}>
            <input
              ref={sliderRef}
              type="range"
              min={ZOOM_MIN}
              max={ZOOM_MAX}
              step={0.01}
              value={zoom}
              onChange={handleZoomChange}
              style={{
                transform: "rotate(-90deg)",
                transformOrigin: "center",
                width: 120,
                height: 20,
                margin: 0,
                cursor: "pointer",
                position: "absolute"
              }}
              aria-label="Zoom"
              onMouseDown={onSliderMouseDown}
              onClick={onSliderClick}
            />
          </div>

          <button
            style={{
              fontSize: 16,
              padding: "2px 8px",
              borderRadius: 5,
              border: "none",
              background: HARBOR_BLUE,
              color: "#fff",
              cursor: "pointer",
              fontWeight: "bold",
              width: 32,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            title="Zoom Out"
            onClick={() => zoomBy(-ZOOM_STEP)}
            onMouseDown={() => startHold(-ZOOM_STEP)}
            onMouseUp={stopHold}
            onMouseLeave={stopHold}
            onTouchStart={() => startHold(-ZOOM_STEP)}
            onTouchEnd={stopHold}
            onTouchCancel={stopHold}
          >−</button>

          <span style={{
            fontSize: 11,
            color: HARBOR_BLUE,
            marginTop: 2,
            fontWeight: 700,
            textAlign: "center"
          }}>
            {(zoom * 100).toFixed(0)}%
          </span>
        </div>

        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onInit={handleInit}
          onNodesChange={handleNodesChange}
          fitView={false}
          minZoom={0.05}
          maxZoom={4}
          panOnDrag
          zoomOnScroll
          onNodeClick={handleNodeClick}
          onPaneClick={handlePaneClick}
          onNodeMouseEnter={handleNodeMouseEnter}
          onNodeMouseMove={handleNodeMouseMove}
          onNodeMouseLeave={handleNodeMouseLeave}
          nodesDraggable={true}
          nodesConnectable={false}
          elementsSelectable={true}
          proOptions={{ hideAttribution: true }}
          style={{ width: "100%", height: "100%" }}
          onMove={handleMove}
        >
          <MiniMap nodeColor={n => String(n.data?.style?.background || HARBOR_INTERACT_BG)} />
          <Background gap={16} color={HARBOR_NEUTRAL_BG_WEAK} />
        </ReactFlow>

        {tooltip && (
          <div
            style={{
              ...tooltipStyle,
              left: tooltip.x,
              top: tooltip.y,
              pointerEvents: 'none',
              position: 'fixed',
            }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(tooltip.content) }}
          />
        )}
      </ReactFlowProvider>
    </div>
  );
}

export default DatabaseViewer;